import React from 'react';
import classnames from 'classnames';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import {FormattedMessage} from 'react-intl';
import setDisplayName from 'recompose/setDisplayName';
import {arrayOf, bool, func, number, shape, string} from 'prop-types';
import {Modal, Grid, Button, Container, Header} from 'semantic-ui-react';
import {filterOptionsFactory, sortOptionsFactory} from 'viewOptions/factories';
import SecretSonicSpinner from 'components/common/SecretSonicSpinner';
import {requestPrizeHistory, closeWinnerDetailModal} from './actions';
import WinnerPrizeHistory from './WinnerPrizeHistory';
import * as ResourceActions from 'resources/actions';
import {EmptyFunc, toggleSort} from 'utils/DBUtils';
import * as ResourceApi from 'api/resources';
import WinnerContact from './WinnerContact';
import * as WinnersActions from './actions';
import {
  createAllResourceItemsSelector,
  createNestedResourceSelector,
  createResourceSelector
} from 'resources/selectors';

class WinnerDetailModal extends React.Component {
  static propTypes = {
    winner: shape({
      userId: number.isRequired,
      email: string.isRequired,
      firstName: string,
      lastName: string,
      address: string,
      address2: string,
      city: string,
      state: string,
      zipCode: string,

    }),
    fetchPrizeHistory: func.isRequired,
    prizeHistory: arrayOf(
      shape({
        id: string.isRequired,
        wonOn: string,
        forfeitDate: string
      })
    ),
    requestSuccess: bool.isRequired,
    isOpen: bool.isRequired,
    handleClose: func.isRequired
  };

  static defaultProps = {
    winner: {},
    prizeHistory: []
  };

  detailViews = [
    WinnerContact,
    WinnerPrizeHistory
  ];

  buttonNames = [
    'modal.winnerDetail.button.contact',
    'modal.winnerDetail.button.prizeHistory'
  ];

  constructor(props) {
    super(props);
    this.state = {
      activeIndex: this.detailViews.indexOf(WinnerContact)
    };
  }

  getButtonFor = viewIndex => {
    const disabled = viewIndex === this.state.activeIndex;
    const onClick = disabled ? EmptyFunc : () => {
      this.setState({activeIndex: viewIndex});
    };

    return (
      <Button
        onClick={onClick}
        primary={!disabled}
        disabled={disabled}>
        <FormattedMessage id={this.buttonNames[viewIndex]} />
      </Button>
    );
  };

  componentWillMount() {
    this.props.fetchPrizeHistory(this.props.winner.userId);
  }

  get header() {
    const {firstName, lastName, userId} = this.props.winner;
    return `${firstName} ${lastName} (${userId})`;
  }

  get content() {
    const historyRequired = this.state.activeIndex === this.detailViews.indexOf(WinnerPrizeHistory);

    // we always have a Winner's contact info before rendering, but their prize histories
    // must be fetched separately. so the contact info can be rendered right away, but if
    // the current view depends on the prize history then we need to consider showing a
    // spinner or a "no results found" message.
    if (historyRequired) {
      const contents = [
        {
          // loading
          content: () => <SecretSonicSpinner messageKey='loading.prizeHistory' />,
          condition: !this.props.requestSuccess && !this.props.prizeHistory.length
        },
        {
          // no results found
          content: () => (
            <Container className='no-results'>
              <Header as='h3' block textAlign='center'>
                <FormattedMessage id='noResults' />
              </Header>
            </Container>
          ),
          condition: this.props.requestSuccess && !this.props.prizeHistory.length
        },
        {
          // show results
          content: () => <WinnerPrizeHistory {...this.props} />,
          condition: this.props.prizeHistory.length
        }
      ];

      const {content} = contents.find(({condition}) => condition);
      return content();
    } else {
      return <WinnerContact {...this.props} />;
    }
  }

  render() {
    return (
      <Modal
        size='small'
        dimmer={true}
        closeIcon={true}
        open={this.props.isOpen}
        closeOnDimmerClick={true}
        onClose={this.props.handleClose}
        className='primary-modal winner-detail-modal'>
        <Modal.Header>
          {this.header}
        </Modal.Header>
        <Modal.Content scrolling={true}>
          {this.content}
        </Modal.Content>
        <Modal.Actions>
          <Grid>
            <Grid.Row columns={2}>
              <Grid.Column width={8} textAlign='left'>
                {this.getButtonFor(this.detailViews.indexOf(WinnerContact))}
              </Grid.Column>
              <Grid.Column width={8} textAlign='right'>
                {this.getButtonFor(this.detailViews.indexOf(WinnerPrizeHistory))}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Actions>
      </Modal>
    );
  }
}

const mapStateToProps = state => {
  const {isOpen, currentWinnerId, currentPrizeHistory, requestSuccess} = state.winnerDetail;

  const getWinner = createResourceSelector(currentWinnerId);
  const getNestedWinner = createNestedResourceSelector('winningEntries[0]', currentWinnerId);

  // for the Prize dashboard, the Winner (really PrizeEntry) is nested on the Prize resource,
  // so we can use a selector to pluck it from there instead of fetching it anew from the server.
  const winner = getNestedWinner(state.resources) || getWinner(state.resources);

  return {
    isOpen,
    winner,
    prizeHistory: currentPrizeHistory,
    requestSuccess
  };
};

const mapDispatchToProps = {
  fetchPrizeHistory: winnerId => {
    const sortOptions = sortOptionsFactory('_created');
    sortOptions.sorts = toggleSort(sortOptions.sorts, '_created');

    const filterOptions = filterOptionsFactory({
      filters: [
        {field: 'userId', value: winnerId},
        {field: 'winner', value: true}
      ]
    });

    return ResourceActions.getAllResourcesAction(ResourceApi.Types.PrizeEntry, sortOptions, filterOptions);
  },
  handleClose: closeWinnerDetailModal
};

export default compose(
  setDisplayName('WinnerDetailModal'),
  connect(mapStateToProps, mapDispatchToProps)
)(WinnerDetailModal);



// WEBPACK FOOTER //
// ./public/components/WinnerDetailModal/WinnerDetailModal.jsx